import React, { useEffect, useState } from 'react';

import moment from 'moment';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {
  ButtonSelect,
  DateTimePicker,
  Grid,
  Input,
  SingleSelect,
  Text,
  colors,
  spacing,
  useStylesFor as stylesFor,
} from '@sine/sine-ui-components';

import sanitizeNumberInput from '../../../helpers/sanitizeNumberInput';

const validToSelectorOptions = [
  { label: 'Elapsed time', value: 'elapsed' },
  { label: 'Specific time', value: 'specific' },
];

const durationUnitOptions = [
  { label: 'Hours', value: 'h' },
  { label: 'Days', value: 'd' },
  { label: 'Weeks', value: 'w' },
  { label: 'Months', value: 'M' },
  { label: 'Years', value: 'Y' },
];

const DurationSelectorContainer = styled.div`
  display: flex;
  align-items: center;

  & > :nth-child(2) {
    margin-left: ${spacing(1)};
    margin-right: ${spacing(2)};
  }

  & > :nth-child(3) {
    margin-top: ${spacing(2.75)};
  }

  ${stylesFor('tablet').andSmaller`
    flex-wrap: wrap;

    & > :nth-child(1) {
      max-width: 50%;
    }

    & > :nth-child(2) {
      max-width: 50%;
      margin-right: 0;
      margin-left: 21px;
    }

    & > :nth-child(3) {
      text-align: center;
      width: 100%;
    }
  `}
`;

const StyledInput = styled(Input)`
  width: 210px;
  white-space: nowrap;
  span {
    height: 0;
  }
  div div {
    margin-top: 0;
  }
`;

const StyledSelect = styled(SingleSelect)`
  min-width: 100px;
  width: 150px;

  span {
    visibility: hidden;
    height: 0;
  }
`;

const StyledDateTimePicker = styled(DateTimePicker)`
  width: 100%;
  & > :nth-child(2) > :nth-child(2) {
    ${stylesFor('tablet').andSmaller`
      min-width: 40%;
    `}
  }

  .react-datepicker__day--disabled.react-datepicker__day--selected {
    background-color: ${colors.red500} !important;
  }
`;

export default function DurationSelector({ form, invitation }) {
  const [validToSelector, setValidToSelector] = useState(validToSelectorOptions[0].value);
  const [durationValue, setDurationValue] = useState(1);
  const [selectedDurationUnitOption, setSelectedDurationUnitOption] = useState(
    invitation?.expiry?.settings?.unit
      ? durationUnitOptions.filter((option) => option.label.toLowerCase() === invitation.expiry.settings.unit)[0]
      : durationUnitOptions[0],
  );
  const [maxValidDate, setMaxValidDate] = useState(moment().add(5, 'year').toDate());
  const [invalidDate, setInvalidDate] = useState(false);
  const minValidDate = moment().subtract(5, 'year').toDate();

  // Set starting values
  useEffect(() => {
    form.onChange('expiry', {
      type: validToSelector,
      settings: {
        value: durationValue,
        unit: selectedDurationUnitOption,
      },
    });
  }, []);

  // Generate the pretty date + update validTo form value.
  useEffect(() => {
    const validTo = moment(form.values.validFrom).add(durationValue, selectedDurationUnitOption.value).toDate();

    setMaxValidDate(moment(form.values.validFrom).add(5, 'year').toDate());

    form.onChange('validTo', validTo);

    if (!moment(validTo).isValid()) {
      setInvalidDate(true);
    }
  }, [form.values.validFrom, durationValue, selectedDurationUnitOption, validToSelector]);

  // Change object structure & save to form for submission.
  useEffect(() => {
    const apiExpectedStructure = {};

    apiExpectedStructure.type = validToSelector;

    apiExpectedStructure.settings = {
      value: durationValue,
      unit: selectedDurationUnitOption.label.toLowerCase(),
    };

    form.onChange('expiry', apiExpectedStructure);
  }, [validToSelector]);

  useEffect(() => {
    const duration = moment
      .duration(moment(form.values.validTo).diff(form.values.validFrom))
      .as(selectedDurationUnitOption.value);
    setDurationValue(Math.max(Math.round(duration), 1));
  }, [form.values.specific]);

  return (
    <Grid container>
      <Grid>
        <ButtonSelect
          options={validToSelectorOptions}
          onChange={setValidToSelector}
          name='expiry.type'
          value={validToSelector}
          alignment='center'
          data-st-ext='expiration-options'
          noSpaceReservation
        />
      </Grid>

      <Grid md={5} data-st-ext='valid-from-grid'>
        <StyledDateTimePicker
          minDate={!invitation ? minValidDate : undefined}
          name='validFrom'
          label='Valid From'
          noSpaceReservation
          data-st-ext='date-picker-valid-from'
        />
      </Grid>
      <Grid md={7} data-st-ext='valid-to-grid' alignItems='stretch'>
        {validToSelector === 'elapsed' && (
          <DurationSelectorContainer>
            <StyledInput
              type='number'
              name='expiry.settings.value'
              label='Expire After'
              onBlur={() => {
                if (durationValue < 1) {
                  form.onChange('expiry.settings.value', 1);
                  setDurationValue(1);
                }
              }}
              onKeyDown={sanitizeNumberInput}
              onChange={setDurationValue}
              value={Math.ceil(durationValue)}
              errors={form.errors.validTo}
              min={1}
              noSpaceReservation
            />
            <StyledSelect
              label='&nbsp;'
              errors={form.errors.validTo}
              name='expiry.settings.unit'
              options={durationUnitOptions}
              onChange={setSelectedDurationUnitOption}
              value={selectedDurationUnitOption}
              noSpaceReservation
              disableAutosort
            />
            <Text>{moment(form.values.validTo).format('LLLL')}</Text>
          </DurationSelectorContainer>
        )}

        {validToSelector === 'specific' && (
          <StyledDateTimePicker
            name='specific'
            maxDate={maxValidDate}
            minDate={moment().toDate()}
            value={!invalidDate ? form.values.validTo : moment().add(1, 'hour').toDate()}
            onChange={(value) => form.onChange('validTo', value)}
            errors={form.errors.validTo}
            label='Valid To'
            noSpaceReservation
            data-st-ext='date-picker-valid-to'
          />
        )}
      </Grid>
    </Grid>
  );
}

DurationSelector.propTypes = {
  form: PropTypes.object,
  invitation: PropTypes.object,
};
